import { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import AppLayout from '@/components/layouts/AppLayout'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import IconButton from '@mui/material/IconButton'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import Editor from '@monaco-editor/react'
import type { DeploymentPackage, DeploymentTypeOption, PackageFileTemplate } from '@/types'

interface Props {
  vendor: { id: number; name: string; slug: string }
  app_entry: { id: number; name: string; slug: string }
  release: { id: number; version: string; status: string; editable: boolean }
  package: DeploymentPackage
  deployment_types: DeploymentTypeOption[]
}

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null
}

export default function DeploymentPackagesEdit({ vendor, app_entry, release, package: pkg, deployment_types }: Props) {
  const [activeTab, setActiveTab] = useState(0)
  const [previewResult, setPreviewResult] = useState<{ template: string; files: { filename: string; content: string }[] } | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewArgs, setPreviewArgs] = useState('{}')
  const basePath = `/vendors/${vendor.slug}/app_entries/${app_entry.slug}/releases/${release.id}`
  const packagePath = `${basePath}/deployment_packages/${pkg.id}`

  // Details form
  const detailsForm = useForm({
    deployment_package: {
      name: pkg.name,
      description: pkg.description || '',
      deployment_type: pkg.deploymentType,
      variant_label: pkg.variantLabel || '',
    },
  })

  // Schema form
  const [schemaText, setSchemaText] = useState(
    pkg.schema ? JSON.stringify(pkg.schema, null, 2) : ''
  )
  const [schemaError, setSchemaError] = useState<string | null>(null)

  // Template form
  const [templateText, setTemplateText] = useState(pkg.template || '')

  // File template form
  const [newFileTemplate, setNewFileTemplate] = useState({
    filename_template: '',
    content_template: '',
    content_type: '',
  })

  const handleDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    detailsForm.patch(packagePath)
  }

  const handleSchemaSave = () => {
    if (!schemaText.trim()) {
      router.patch(packagePath, {
        deployment_package: { schema: null },
      })
      return
    }
    try {
      const parsed = JSON.parse(schemaText)
      setSchemaError(null)
      router.patch(packagePath, {
        deployment_package: { schema: parsed },
      })
    } catch (e) {
      setSchemaError(`Invalid JSON: ${(e as Error).message}`)
    }
  }

  const handleTemplateSave = () => {
    router.patch(packagePath, {
      deployment_package: { template: templateText },
    })
  }

  const handleAddFileTemplate = (e: React.FormEvent) => {
    e.preventDefault()
    router.post(`${packagePath}/file_templates`, {
      package_file_template: newFileTemplate,
    })
    setNewFileTemplate({ filename_template: '', content_template: '', content_type: '' })
  }

  const handleDeleteFileTemplate = (ft: PackageFileTemplate) => {
    router.delete(`${packagePath}/file_templates/${ft.id}`)
  }

  const handlePreview = async () => {
    setPreviewError(null)
    setPreviewResult(null)
    try {
      const args = JSON.parse(previewArgs)
      const response = await fetch(`${packagePath}/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({ args }),
      })
      const data = await response.json()
      if (response.ok) {
        setPreviewResult(data.preview)
      } else {
        setPreviewError(data.error || 'Preview failed')
      }
    } catch (e) {
      setPreviewError(`Error: ${(e as Error).message}`)
    }
  }

  return (
    <AppLayout>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton
          onClick={() => router.visit(`${basePath}/deployment_packages`)}
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5">
            Edit Package: {pkg.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {vendor.name} / {app_entry.name} / v{release.version}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ px: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Details" />
          <Tab label="Schema" />
          <Tab label="Template" />
          <Tab label="File Templates" />
          <Tab label="Preview" />
        </Tabs>
      </Paper>

      {/* Details Tab */}
      <TabPanel value={activeTab} index={0}>
        <Paper sx={{ p: 3, maxWidth: 600 }}>
          <form onSubmit={handleDetailsSubmit}>
            <TextField
              label="Name"
              fullWidth
              required
              margin="normal"
              value={detailsForm.data.deployment_package.name}
              onChange={(e) =>
                detailsForm.setData('deployment_package', {
                  ...detailsForm.data.deployment_package,
                  name: e.target.value,
                })
              }
            />
            <TextField
              label="Deployment Type"
              fullWidth
              required
              select
              margin="normal"
              value={detailsForm.data.deployment_package.deployment_type}
              onChange={(e) =>
                detailsForm.setData('deployment_package', {
                  ...detailsForm.data.deployment_package,
                  deployment_type: e.target.value,
                })
              }
            >
              {deployment_types.map((dt) => (
                <MenuItem key={dt.value} value={dt.value}>
                  {dt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Variant Label"
              fullWidth
              margin="normal"
              value={detailsForm.data.deployment_package.variant_label}
              onChange={(e) =>
                detailsForm.setData('deployment_package', {
                  ...detailsForm.data.deployment_package,
                  variant_label: e.target.value,
                })
              }
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              margin="normal"
              value={detailsForm.data.deployment_package.description}
              onChange={(e) =>
                detailsForm.setData('deployment_package', {
                  ...detailsForm.data.deployment_package,
                  description: e.target.value,
                })
              }
            />
            <Box sx={{ mt: 2 }}>
              <Button type="submit" variant="contained" disabled={detailsForm.processing}>
                Save Details
              </Button>
            </Box>
          </form>
        </Paper>
      </TabPanel>

      {/* Schema Tab */}
      <TabPanel value={activeTab} index={1}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Package Schema (JSON) — Groups &gt; Segments &gt; Fields
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define the configuration schema. Must conform to the marketplace meta-schema format.
          </Typography>
          {schemaError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {schemaError}
            </Alert>
          )}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Editor
              height="400px"
              language="json"
              value={schemaText}
              onChange={(v) => setSchemaText(v || '')}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                tabSize: 2,
                scrollBeyondLastLine: false,
              }}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleSchemaSave}>
              Save Schema
            </Button>
          </Box>
        </Paper>
      </TabPanel>

      {/* Template Tab */}
      <TabPanel value={activeTab} index={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Liquid Template
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The main deployment template using Liquid syntax. Schema field values are available as template variables.
          </Typography>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Editor
              height="400px"
              language="yaml"
              value={templateText}
              onChange={(v) => setTemplateText(v || '')}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                tabSize: 2,
                scrollBeyondLastLine: false,
              }}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleTemplateSave}>
              Save Template
            </Button>
          </Box>
        </Paper>
      </TabPanel>

      {/* File Templates Tab */}
      <TabPanel value={activeTab} index={3}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Additional File Templates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define extra files generated alongside the main template. Filenames and content support Liquid syntax.
          </Typography>

          {pkg.fileTemplates && pkg.fileTemplates.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {pkg.fileTemplates.map((ft) => (
                <Paper key={ft.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">{ft.filenameTemplate}</Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteFileTemplate(ft)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {ft.contentType && (
                    <Typography variant="caption" color="text.secondary">
                      Content-Type: {ft.contentType}
                    </Typography>
                  )}
                  <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Editor
                      height="150px"
                      language="yaml"
                      value={ft.contentTemplate}
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        fontSize: 12,
                        scrollBeyondLastLine: false,
                      }}
                    />
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Add New File Template
          </Typography>
          <form onSubmit={handleAddFileTemplate}>
            <TextField
              label="Filename Template"
              fullWidth
              required
              margin="normal"
              size="small"
              value={newFileTemplate.filename_template}
              onChange={(e) =>
                setNewFileTemplate({ ...newFileTemplate, filename_template: e.target.value })
              }
              helperText="e.g., .env, docker-compose.override.yml"
            />
            <TextField
              label="Content Type"
              fullWidth
              margin="normal"
              size="small"
              value={newFileTemplate.content_type}
              onChange={(e) =>
                setNewFileTemplate({ ...newFileTemplate, content_type: e.target.value })
              }
              helperText="e.g., text/yaml, text/plain"
            />
            <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Editor
                height="200px"
                language="yaml"
                value={newFileTemplate.content_template}
                onChange={(v) =>
                  setNewFileTemplate({ ...newFileTemplate, content_template: v || '' })
                }
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  tabSize: 2,
                  scrollBeyondLastLine: false,
                }}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={!newFileTemplate.filename_template || !newFileTemplate.content_template}
              >
                Add File Template
              </Button>
            </Box>
          </form>
        </Paper>
      </TabPanel>

      {/* Preview Tab */}
      <TabPanel value={activeTab} index={4}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Template Preview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter sample arguments as JSON to preview the rendered template output.
          </Typography>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
            <Editor
              height="150px"
              language="json"
              value={previewArgs}
              onChange={(v) => setPreviewArgs(v || '{}')}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                tabSize: 2,
                scrollBeyondLastLine: false,
              }}
            />
          </Box>
          <Button variant="contained" onClick={handlePreview}>
            Render Preview
          </Button>

          {previewError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {previewError}
            </Alert>
          )}

          {previewResult && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Main Template Output
              </Typography>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                <Editor
                  height="300px"
                  language="yaml"
                  value={previewResult.template}
                  options={{
                    readOnly: true,
                    minimap: { enabled: false },
                    fontSize: 13,
                    scrollBeyondLastLine: false,
                  }}
                />
              </Box>

              {previewResult.files.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    File Template Outputs
                  </Typography>
                  {previewResult.files.map((file, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1 }}>
                      <Typography variant="subtitle2">{file.filename}</Typography>
                      <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Editor
                          height="150px"
                          language="yaml"
                          value={file.content}
                          options={{
                            readOnly: true,
                            minimap: { enabled: false },
                            fontSize: 12,
                            scrollBeyondLastLine: false,
                          }}
                        />
                      </Box>
                    </Paper>
                  ))}
                </>
              )}
            </Box>
          )}
        </Paper>
      </TabPanel>
    </AppLayout>
  )
}
